/**
 * Template Variable Registry and Utilities
 * 
 * Provides a comprehensive registry of available template variables,
 * auto-extraction from templates, and variable management utilities.
 */

import { TemplateVariable } from '../types/wordTemplate'

/**
 * Variable Registry - Comprehensive list of all available template variables
 * Organized by category with full metadata
 */
export const VARIABLE_REGISTRY: Record<string, TemplateVariable[]> = {
  patient: [
    {
      name: 'patient.id',
      label: 'Patient ID',
      description: 'Unique patient identifier',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.patientNumber',
      label: 'Patient Number',
      description: 'Master Patient Index number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.firstName',
      label: 'First Name',
      description: 'Patient first name',
      type: 'string',
      category: 'patient',
      required: true,
    },
    {
      name: 'patient.middleName',
      label: 'Middle Name',
      description: 'Patient middle name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.lastName',
      label: 'Last Name',
      description: 'Patient last name',
      type: 'string',
      category: 'patient',
      required: true,
    },
    {
      name: 'patient.fullName',
      label: 'Full Name',
      description: 'Complete patient name (first + middle + last)',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.dateOfBirth',
      label: 'Date of Birth',
      description: 'Date of birth in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.dateOfBirthFormatted',
      label: 'Date of Birth (Formatted)',
      description: 'Formatted date of birth (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.age',
      label: 'Age',
      description: 'Calculated age in years',
      type: 'number',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.gender',
      label: 'Gender',
      description: 'Patient gender',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.maritalStatus',
      label: 'Marital Status',
      description: 'Marital status',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.email',
      label: 'Email',
      description: 'Primary email address',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.phone',
      label: 'Phone',
      description: 'Primary phone number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.alternatePhone',
      label: 'Alternate Phone',
      description: 'Alternate phone number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.street',
      label: 'Street Address',
      description: 'Street address line',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.city',
      label: 'City',
      description: 'City name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.state',
      label: 'State/Province',
      description: 'State or province',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.postalCode',
      label: 'Postal Code',
      description: 'Postal/ZIP code',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.country',
      label: 'Country',
      description: 'Country name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.address.fullAddress',
      label: 'Full Address',
      description: 'Complete formatted address',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.emergencyContact.name',
      label: 'Emergency Contact Name',
      description: 'Emergency contact full name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.emergencyContact.relationship',
      label: 'Emergency Contact Relationship',
      description: 'Relationship to patient',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.emergencyContact.phone',
      label: 'Emergency Contact Phone',
      description: 'Emergency contact phone number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.emergencyContact.email',
      label: 'Emergency Contact Email',
      description: 'Emergency contact email address',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.bloodType',
      label: 'Blood Type',
      description: 'Patient blood type',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.allergies',
      label: 'Allergies',
      description: 'Array of allergies',
      type: 'array',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.allergiesList',
      label: 'Allergies (List)',
      description: 'Comma-separated list of allergies',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.currentMedications',
      label: 'Current Medications',
      description: 'Array of current medications',
      type: 'array',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.currentMedicationsList',
      label: 'Current Medications (List)',
      description: 'Comma-separated list of current medications',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.pastMedications',
      label: 'Past Medications',
      description: 'Array of past medications',
      type: 'array',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.chronicConditions',
      label: 'Chronic Conditions',
      description: 'Array of chronic conditions',
      type: 'array',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.chronicConditionsList',
      label: 'Chronic Conditions (List)',
      description: 'Comma-separated list of chronic conditions',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.medicalHistory',
      label: 'Medical History',
      description: 'Medical history text',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.provider',
      label: 'Insurance Provider',
      description: 'Insurance provider name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.policyNumber',
      label: 'Insurance Policy Number',
      description: 'Insurance policy number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.groupNumber',
      label: 'Insurance Group Number',
      description: 'Insurance group number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.effectiveDate',
      label: 'Insurance Effective Date',
      description: 'Insurance effective date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.effectiveDateFormatted',
      label: 'Insurance Effective Date (Formatted)',
      description: 'Formatted insurance effective date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.expirationDate',
      label: 'Insurance Expiration Date',
      description: 'Insurance expiration date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.insurance.expirationDateFormatted',
      label: 'Insurance Expiration Date (Formatted)',
      description: 'Formatted insurance expiration date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.ppsNumber',
      label: 'PPS Number',
      description: 'Personal Public Service number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.gmsNumber',
      label: 'GMS Number',
      description: 'GMS card number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.patientType',
      label: 'Patient Type',
      description: 'Patient type (private, gms, doctorVisitCard, other)',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.number',
      label: 'GMS Card Number',
      description: 'GMS card number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.reviewDate',
      label: 'GMS Review Date',
      description: 'GMS review date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.reviewDateFormatted',
      label: 'GMS Review Date (Formatted)',
      description: 'Formatted GMS review date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.doctorNumber',
      label: 'GMS Doctor Number',
      description: 'GMS doctor number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.distanceCode',
      label: 'GMS Distance Code',
      description: 'GMS distance code',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.gms.dispensingStatus',
      label: 'GMS Dispensing Status',
      description: 'GMS dispensing status',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.doctorVisitCard.number',
      label: 'Doctor Visit Card Number',
      description: 'Doctor visit card number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.doctorVisitCard.reviewDate',
      label: 'Doctor Visit Card Review Date',
      description: 'Doctor visit card review date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.doctorVisitCard.reviewDateFormatted',
      label: 'Doctor Visit Card Review Date (Formatted)',
      description: 'Formatted doctor visit card review date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.doctorVisitCard.doctorNumber',
      label: 'Doctor Visit Card Doctor Number',
      description: 'Doctor visit card doctor number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.doctorVisitCard.distanceCode',
      label: 'Doctor Visit Card Distance Code',
      description: 'Doctor visit card distance code',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.dps.number',
      label: 'DPS Number',
      description: 'Drug Payment Scheme number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.dps.expiryDate',
      label: 'DPS Expiry Date',
      description: 'DPS expiry date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.dps.expiryDateFormatted',
      label: 'DPS Expiry Date (Formatted)',
      description: 'Formatted DPS expiry date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.privateInsurance.company',
      label: 'Private Insurance Company',
      description: 'Private insurance company name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.privateInsurance.membershipNumber',
      label: 'Private Insurance Membership Number',
      description: 'Private insurance membership number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.privateInsurance.coverType',
      label: 'Private Insurance Cover Type',
      description: 'Private insurance cover type',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.companySchemes.anPost',
      label: 'An Post Scheme',
      description: 'An Post company scheme membership',
      type: 'boolean',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.companySchemes.garda',
      label: 'Garda Scheme',
      description: 'Garda company scheme membership',
      type: 'boolean',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.coverage.companySchemes.other',
      label: 'Other Company Scheme',
      description: 'Other company scheme details',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.identifiersExtended.ppsNumber',
      label: 'Extended PPS Number',
      description: 'PPS number from extended identifiers',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.identifiersExtended.ihiNumber',
      label: 'IHI Number',
      description: 'Individual Health Identifier number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.identifiersExtended.internalNumber',
      label: 'Internal Number',
      description: 'Internal patient number',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.socioCultural.occupation',
      label: 'Occupation',
      description: 'Patient occupation',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.socioCultural.maritalStatus',
      label: 'Marital Status (Socio-Cultural)',
      description: 'Marital status from socio-cultural data',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.socioCultural.religion',
      label: 'Religion',
      description: 'Patient religion',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.socioCultural.ethnicity',
      label: 'Ethnicity',
      description: 'Patient ethnicity',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.registeredGpName',
      label: 'Registered GP Name',
      description: 'Name of registered GP',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.usualGpName',
      label: 'Usual GP Name',
      description: 'Name of usual GP',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.branchName',
      label: 'Branch Name',
      description: 'Practice branch name',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.registrationDate',
      label: 'Registration Date',
      description: 'Practice registration date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.registrationDateFormatted',
      label: 'Registration Date (Formatted)',
      description: 'Formatted practice registration date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.deregistrationDate',
      label: 'Deregistration Date',
      description: 'Practice deregistration date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.deregistrationDateFormatted',
      label: 'Deregistration Date (Formatted)',
      description: 'Formatted practice deregistration date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.deregistrationReason',
      label: 'Deregistration Reason',
      description: 'Reason for practice deregistration',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.primaryCareTeamEnrolmentStatus',
      label: 'Primary Care Team Enrolment Status',
      description: 'Primary care team enrolment status',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.primaryCareTeamEnrolmentDate',
      label: 'Primary Care Team Enrolment Date',
      description: 'Primary care team enrolment date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.primaryCareTeamEnrolmentDateFormatted',
      label: 'Primary Care Team Enrolment Date (Formatted)',
      description: 'Formatted primary care team enrolment date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.primaryCareTeamDeEnrolmentDate',
      label: 'Primary Care Team De-Enrolment Date',
      description: 'Primary care team de-enrolment date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.practiceRegistration.primaryCareTeamDeEnrolmentDateFormatted',
      label: 'Primary Care Team De-Enrolment Date (Formatted)',
      description: 'Formatted primary care team de-enrolment date (DD/MM/YYYY)',
      type: 'date',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.communicationSupport.preferredLanguage',
      label: 'Preferred Language',
      description: 'Patient preferred language',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.communicationSupport.interpreterRequired',
      label: 'Interpreter Required',
      description: 'Whether interpreter is required',
      type: 'boolean',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.communicationSupport.transportNeeds',
      label: 'Transport Needs',
      description: 'Patient transport needs',
      type: 'string',
      category: 'patient',
      required: false,
    },
    {
      name: 'patient.communicationSupport.advocacyNeeds',
      label: 'Advocacy Needs',
      description: 'Patient advocacy needs',
      type: 'string',
      category: 'patient',
      required: false,
    },
  ],
  system: [
    {
      name: 'system.currentDate',
      label: 'Current Date',
      description: 'Current date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'system',
      required: false,
    },
    {
      name: 'system.currentDateFormatted',
      label: 'Current Date (Formatted)',
      description: 'Formatted current date (DD/MM/YYYY)',
      type: 'date',
      category: 'system',
      required: false,
    },
    {
      name: 'system.currentDateTime',
      label: 'Current Date & Time',
      description: 'Current date and time in ISO format',
      type: 'date',
      category: 'system',
      required: false,
    },
  ],
  consultation: [
    {
      name: 'consultation.id',
      label: 'Consultation ID',
      description: 'Unique consultation identifier',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'consultation.date',
      label: 'Consultation Date',
      description: 'Date of consultation',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'consultation.notes',
      label: 'Consultation Notes',
      description: 'Consultation notes and findings',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.recordedAt',
      label: 'Latest Vital Recorded At',
      description: 'Latest vital signs recorded timestamp',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.recordedAtFormatted',
      label: 'Latest Vital Recorded At (Formatted)',
      description: 'Formatted recorded date (DD/MM/YYYY)',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.bloodPressure.systolic',
      label: 'Latest Systolic',
      description: 'Latest systolic blood pressure',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.bloodPressure.diastolic',
      label: 'Latest Diastolic',
      description: 'Latest diastolic blood pressure',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.heartRate.value',
      label: 'Latest Heart Rate',
      description: 'Latest heart rate value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.heartRate.unit',
      label: 'Latest Heart Rate Unit',
      description: 'Latest heart rate unit',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.temperature.value',
      label: 'Latest Temperature',
      description: 'Latest temperature value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.temperature.unit',
      label: 'Latest Temperature Unit',
      description: 'Latest temperature unit',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.weight.value',
      label: 'Latest Weight',
      description: 'Latest weight value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.weight.unit',
      label: 'Latest Weight Unit',
      description: 'Latest weight unit',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.height.value',
      label: 'Latest Height',
      description: 'Latest height value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.height.unit',
      label: 'Latest Height Unit',
      description: 'Latest height unit',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.bmi',
      label: 'Latest BMI',
      description: 'Latest BMI value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.oxygenSaturation.value',
      label: 'Latest Oxygen Saturation',
      description: 'Latest oxygen saturation value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.respiratoryRate.value',
      label: 'Latest Respiratory Rate',
      description: 'Latest respiratory rate value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.painSeverity.value',
      label: 'Latest Pain Score',
      description: 'Latest pain severity score',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latest.notes',
      label: 'Latest Vital Notes',
      description: 'Latest vital signs notes',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.latestSummary',
      label: 'Latest Vital Summary',
      description: 'Summary string of latest vitals',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.date',
      label: 'Latest Immunisation Date',
      description: 'Latest immunisation date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.dateFormatted',
      label: 'Latest Immunisation Date (Formatted)',
      description: 'Formatted latest immunisation date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.type',
      label: 'Latest Immunisation Type',
      description: 'Latest immunisation type',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.batchNumber',
      label: 'Latest Immunisation Batch Number',
      description: 'Latest immunisation batch number',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.manufacturer',
      label: 'Latest Immunisation Manufacturer',
      description: 'Latest immunisation manufacturer',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.expiryDate',
      label: 'Latest Immunisation Expiry Date',
      description: 'Latest immunisation expiry date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.expiryDateFormatted',
      label: 'Latest Immunisation Expiry Date (Formatted)',
      description: 'Formatted immunisation expiry date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.siteGiven',
      label: 'Latest Immunisation Site',
      description: 'Latest immunisation site given',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.methodOfAdmin',
      label: 'Latest Immunisation Method',
      description: 'Latest immunisation method of administration',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.dose.value',
      label: 'Latest Immunisation Dose',
      description: 'Latest immunisation dose value',
      type: 'number',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.dose.unit',
      label: 'Latest Immunisation Dose Unit',
      description: 'Latest immunisation dose unit',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.administeredByName',
      label: 'Latest Immunisation Administered By',
      description: 'Name of clinician who administered latest immunisation',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latest.notes',
      label: 'Latest Immunisation Notes',
      description: 'Latest immunisation notes',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.latestSummary',
      label: 'Latest Immunisation Summary',
      description: 'Summary string of latest immunisation',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.name',
      label: 'Latest Medication Name',
      description: 'Latest medication name',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.doseText',
      label: 'Latest Medication Dose Text',
      description: 'Latest medication dose text',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.route',
      label: 'Latest Medication Route',
      description: 'Latest medication route',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.frequency.text',
      label: 'Latest Medication Frequency',
      description: 'Latest medication frequency text',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.startDate',
      label: 'Latest Medication Start Date',
      description: 'Latest medication start date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.endDate',
      label: 'Latest Medication End Date',
      description: 'Latest medication end date',
      type: 'date',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.status',
      label: 'Latest Medication Status',
      description: 'Latest medication status',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.prescriberName',
      label: 'Latest Medication Prescriber',
      description: 'Prescriber name for latest medication',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latest.indication',
      label: 'Latest Medication Indication',
      description: 'Indication for latest medication',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.latestSummary',
      label: 'Latest Medication Summary',
      description: 'Summary string of latest medication',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.activeList',
      label: 'Active Medications',
      description: 'Array of active medication names',
      type: 'array',
      category: 'clinical',
      required: false,
    },
    {
      name: 'medications.activeListText',
      label: 'Active Medications (List)',
      description: 'Comma-separated list of active medications',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'immunisations.listSummary',
      label: 'Immunisation Summary List',
      description: 'Comma-separated summary list of immunisations',
      type: 'string',
      category: 'clinical',
      required: false,
    },
    {
      name: 'vitals.listSummary',
      label: 'Vital Signs Summary List',
      description: 'Comma-separated summary list of vitals',
      type: 'string',
      category: 'clinical',
      required: false,
    },
  ],
  referral: [
    {
      name: 'referral.id',
      label: 'Referral ID',
      description: 'Unique referral identifier',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.patientId',
      label: 'Patient ID',
      description: 'Patient identifier linked to the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.consultationId',
      label: 'Consultation ID',
      description: 'Consultation identifier linked to the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referringProviderId',
      label: 'Referring Provider ID',
      description: 'Internal provider identifier for the referrer',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referringProviderName',
      label: 'Referring Provider Name',
      description: 'Name of the referring provider',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referredToProviderId',
      label: 'Referred To Provider ID',
      description: 'Provider directory identifier if selected',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referredToProviderName',
      label: 'Referred To Provider Name',
      description: 'Name of the receiving provider',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referredToProviderType',
      label: 'Referred To Provider Type',
      description: 'Receiving provider type (internal/external)',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referralDate',
      label: 'Referral Date',
      description: 'Referral date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.referralDateFormatted',
      label: 'Referral Date (Formatted)',
      description: 'Formatted referral date (DD/MM/YYYY)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.priority',
      label: 'Referral Priority',
      description: 'Referral priority (routine, urgent, emergency)',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.status',
      label: 'Referral Status',
      description: 'Referral status (draft, sent, acknowledged, completed, cancelled)',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.reason',
      label: 'Referral Reason',
      description: 'Reason for referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.notes',
      label: 'Referral Notes',
      description: 'Additional referral notes',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.templateId',
      label: 'Template ID',
      description: 'Template identifier used for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.templateName',
      label: 'Template Name',
      description: 'Template name used for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.documentContent',
      label: 'Referral Document Content',
      description: 'Generated referral document content (base64)',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationMethod',
      label: 'Communication Method',
      description: 'Method used to communicate the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.followUpRequired',
      label: 'Follow Up Required',
      description: 'Whether follow up is required',
      type: 'boolean',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.followUpDate',
      label: 'Follow Up Date',
      description: 'Follow up date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.followUpDateFormatted',
      label: 'Follow Up Date (Formatted)',
      description: 'Formatted follow up date (DD/MM/YYYY)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.createdAt',
      label: 'Created At',
      description: 'Referral creation timestamp',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.updatedAt',
      label: 'Updated At',
      description: 'Referral update timestamp',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.createdBy',
      label: 'Created By',
      description: 'User who created the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.updatedBy',
      label: 'Updated By',
      description: 'User who last updated the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.name',
      label: 'Provider Name',
      description: 'Provider name',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.firstName',
      label: 'Provider First Name',
      description: 'Provider first name',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.lastName',
      label: 'Provider Last Name',
      description: 'Provider last name',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.title',
      label: 'Provider Title',
      description: 'Provider title',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.fullName',
      label: 'Provider Full Name',
      description: 'Provider full name (first + last)',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.organization',
      label: 'Provider Organization',
      description: 'Provider organization',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.specialty',
      label: 'Provider Specialty',
      description: 'Provider specialty',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.street',
      label: 'Provider Street Address',
      description: 'Provider street address',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.city',
      label: 'Provider City',
      description: 'Provider city',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.state',
      label: 'Provider State/Province',
      description: 'Provider state or province',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.postalCode',
      label: 'Provider Postal Code',
      description: 'Provider postal code',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.country',
      label: 'Provider Country',
      description: 'Provider country',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.address.fullAddress',
      label: 'Provider Full Address',
      description: 'Provider full formatted address',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.phone',
      label: 'Provider Phone',
      description: 'Provider phone number',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.email',
      label: 'Provider Email',
      description: 'Provider email address',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.providerDetails.healthmailAddress',
      label: 'Provider Healthmail Address',
      description: 'Provider Healthmail address',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.presentingComplaint',
      label: 'Presenting Complaint',
      description: 'Presenting complaint',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.historyOfPresentingComplaint',
      label: 'History of Presenting Complaint',
      description: 'History of presenting complaint',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.pastMedicalHistory',
      label: 'Past Medical History',
      description: 'Past medical history',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.currentMedications',
      label: 'Current Medications',
      description: 'Array of current medications',
      type: 'array',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.currentMedicationsList',
      label: 'Current Medications (List)',
      description: 'Comma-separated list of current medications',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.allergies',
      label: 'Allergies',
      description: 'Array of allergies',
      type: 'array',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.allergiesList',
      label: 'Allergies (List)',
      description: 'Comma-separated list of allergies',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.examinationFindings',
      label: 'Examination Findings',
      description: 'Examination findings',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.investigations',
      label: 'Investigations',
      description: 'Investigations',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.diagnosis',
      label: 'Diagnosis',
      description: 'Array of diagnoses',
      type: 'array',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.diagnosisList',
      label: 'Diagnosis (List)',
      description: 'Comma-separated list of diagnoses',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.treatmentToDate',
      label: 'Treatment to Date',
      description: 'Treatment to date',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.clinicalDetails.specificQuestions',
      label: 'Specific Questions',
      description: 'Questions for receiving provider',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.insuranceProvider',
      label: 'Referral Insurance Provider',
      description: 'Insurance provider for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.policyNumber',
      label: 'Referral Policy Number',
      description: 'Insurance policy number for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.groupNumber',
      label: 'Referral Group Number',
      description: 'Insurance group number for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.authorizationNumber',
      label: 'Referral Authorization Number',
      description: 'Insurance authorization number for the referral',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.expiryDate',
      label: 'Referral Insurance Expiry Date',
      description: 'Insurance expiry date in ISO format (YYYY-MM-DD)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.insuranceInformation.expiryDateFormatted',
      label: 'Referral Insurance Expiry Date (Formatted)',
      description: 'Formatted insurance expiry date (DD/MM/YYYY)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.method',
      label: 'Communication Method (Status)',
      description: 'Communication method in status tracking',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.sentAt',
      label: 'Communication Sent At',
      description: 'Timestamp for when referral was sent',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.sentAtFormatted',
      label: 'Communication Sent At (Formatted)',
      description: 'Formatted sent timestamp (DD/MM/YYYY)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.acknowledgedAt',
      label: 'Communication Acknowledged At',
      description: 'Timestamp for when referral was acknowledged',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.acknowledgedAtFormatted',
      label: 'Communication Acknowledged At (Formatted)',
      description: 'Formatted acknowledgement timestamp (DD/MM/YYYY)',
      type: 'date',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.healthmailMessageId',
      label: 'Healthmail Message ID',
      description: 'Healthmail message identifier',
      type: 'string',
      category: 'referral',
      required: false,
    },
    {
      name: 'referral.communicationStatus.errorMessage',
      label: 'Communication Error Message',
      description: 'Error message for communication failures',
      type: 'string',
      category: 'referral',
      required: false,
    },
  ],
}

/**
 * Get all variables from registry
 */
export function getAllVariables(): TemplateVariable[] {
  return Object.values(VARIABLE_REGISTRY).flat()
}

/**
 * Get variables by category
 */
export function getVariablesByCategory(category: string): TemplateVariable[] {
  return VARIABLE_REGISTRY[category] || []
}

/**
 * Find variable by name
 */
export function findVariable(name: string): TemplateVariable | undefined {
  return getAllVariables().find(v => v.name === name)
}

/**
 * Extract variables from template content
 * Accepts either base64-encoded template content or XML string
 * If base64, decodes and extracts content.xml from ZIP first
 */
export async function extractVariablesFromContent(content: string, isBase64: boolean = false): Promise<string[]> {
  const variables = new Set<string>()
  
  let contentXml: string
  
  if (isBase64) {
    // Decode base64 and extract content.xml from ZIP
    try {
      const binaryString = atob(content)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      
      // Use JSZip to extract content.xml
      const JSZip = (await import('jszip')).default
      const zip = await JSZip.loadAsync(bytes)
      const contentFile = zip.file('content.xml')
      
      if (!contentFile) {
        console.warn('content.xml not found in template')
        return []
      }
      
      contentXml = await contentFile.async('string')
    } catch (error) {
      console.error('Error extracting content.xml from template:', error)
      return []
    }
  } else {
    // Assume it's already XML content
    contentXml = content
  }
  
  // Match variables in format {variable} - be more strict about what we accept
  // Only match variables that look like valid variable names
  const variableRegex = /\{([^}]+)\}/g
  let match
  
  // Valid variable name pattern: starts with letter, contains only alphanumeric, dots, and underscores
  const validVarPattern = /^[a-zA-Z][a-zA-Z0-9._]*$/
  
  while ((match = variableRegex.exec(contentXml)) !== null) {
    const varPath = match[1]?.trim()
    if (varPath) {
      // Skip control structures
      if (varPath.startsWith('#') || varPath.startsWith('/') || varPath.startsWith(':')) {
        continue
      }
      
      // Only add if it looks like a valid variable name
      // Must start with patient, system, consultation, or referral and have valid characters
      if (validVarPattern.test(varPath) && 
          (varPath.startsWith('patient.') ||
           varPath.startsWith('system.') ||
           varPath.startsWith('consultation.') ||
           varPath.startsWith('referral.') ||
           varPath.startsWith('vitals.') ||
           varPath.startsWith('immunisations.') ||
           varPath.startsWith('medications.'))) {
        variables.add(varPath)
      }
    }
  }
  
  return Array.from(variables)
}

/**
 * Enrich extracted variables with metadata from registry
 */
export async function enrichVariables(variableNames: string[]): Promise<TemplateVariable[]> {
  return variableNames.map(name => {
    // Filter out invalid variable names (non-printable characters, etc.)
    if (!/^[a-zA-Z0-9._]+$/.test(name)) {
      return null
    }
    
    const registryVar = findVariable(name)
    if (registryVar) {
      return registryVar
    }
    
    // Create a variable entry for unknown variables
    const parts = name.split('.')
    const category = parts[0] === 'patient' ? 'patient' 
                   : parts[0] === 'system' ? 'system'
                   : parts[0] === 'consultation' ? 'clinical'
                   : parts[0] === 'referral' ? 'referral'
                   : parts[0] === 'vitals' ? 'clinical'
                   : parts[0] === 'immunisations' ? 'clinical'
                   : parts[0] === 'medications' ? 'clinical'
                   : 'custom'
    
    return {
      name,
      label: name.split('.').slice(1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' '),
      description: `Custom variable: ${name}`,
      type: 'string',
      category: category as any,
      required: false,
    }
  }).filter((v): v is TemplateVariable => v !== null)
}

/**
 * Validate variables against registry
 * Returns list of unknown/invalid variables
 */
export function validateVariables(variableNames: string[]): {
  valid: string[]
  invalid: string[]
  unknown: string[]
} {
  const valid: string[] = []
  const invalid: string[] = []
  const unknown: string[] = []
  
  const allRegistryVars = getAllVariables().map(v => v.name)
  
  // Valid variable name pattern: starts with letter, contains only alphanumeric, dots, and underscores
  const validNamePattern = /^[a-zA-Z][a-zA-Z0-9._]*$/
  
  variableNames.forEach(name => {
    // First check if it's a valid variable name format
    if (!validNamePattern.test(name)) {
      invalid.push(name)
      return
    }
    
    if (allRegistryVars.includes(name)) {
      valid.push(name)
    } else if (
      name.startsWith('patient.') ||
      name.startsWith('system.') ||
      name.startsWith('consultation.') ||
      name.startsWith('referral.') ||
      name.startsWith('vitals.') ||
      name.startsWith('immunisations.') ||
      name.startsWith('medications.')
    ) {
      unknown.push(name) // Valid format but not in registry
    } else {
      invalid.push(name) // Invalid format
    }
  })
  
  return { valid, invalid, unknown }
}

/**
 * Search variables by query
 */
export function searchVariables(query: string): TemplateVariable[] {
  const lowerQuery = query.toLowerCase()
  return getAllVariables().filter(v => 
    v.name.toLowerCase().includes(lowerQuery) ||
    v.label.toLowerCase().includes(lowerQuery) ||
    (v.description && v.description.toLowerCase().includes(lowerQuery))
  )
}

/**
 * Group variables by category
 */
export function groupVariablesByCategory(variables: TemplateVariable[]): Record<string, TemplateVariable[]> {
  const grouped: Record<string, TemplateVariable[]> = {}
  
  variables.forEach(v => {
    if (!grouped[v.category]) {
      grouped[v.category] = []
    }
    grouped[v.category].push(v)
  })
  
  return grouped
}






























